import { FluentProvider } from '@fluentui/react-components';
import { app } from '@microsoft/teams-js';
import { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { IntlProvider } from 'react-intl';
import { Provider as ReduxProvider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';

import { useSelector } from '@tdp';
import { localizationSlice } from '@tdp/common/localization';
import { themeSlice } from '@tdp/common/theme';

import { StartupLoadingScreen } from './components/startupLoadingScreen';
import { DevPortalRoot, compositionRoot } from './compositionRoot';
import { startup } from './startup';
import { DeveloperPortalRootContext } from './TdpServicesContext';
import { loadBuildConfig } from './util/buildConfig';
import { lazify } from './util/lazify';

const WithLocaleProvider = (props: DevPortalRoot) => {
  const localeMessages = useSelector(localizationSlice.selectors.currentLocale);
  const activeTheme = useSelector(themeSlice.selectors.getFluentTheme);
  const currentLocaleName =
    localeMessages.locale === 'en-dbg' ? 'en' : localeMessages.locale;
  return (
    <IntlProvider
      messages={localeMessages.strings}
      locale={currentLocaleName}
      defaultLocale="en"
    >
      <FluentProvider theme={activeTheme} dir="ltr" style={{ height: '100%' }}>
        <RouterProvider router={props.router} />
      </FluentProvider>
    </IntlProvider>
  );
};


// Entrypoint for the app.
function main() {
  const buildConfig = loadBuildConfig();
  // NOTE: this is a promise for the app root, not the actual root object.
  const appRootPromise = compositionRoot(buildConfig).then(async (app) => {
    await startup(app, buildConfig);
    return app;
  });
  // Since the services are loaded asynchronously, we need to lazily load the base application
  const DeveloperPortal = lazify((props: DevPortalRoot) => {
    const { store } = props;

    return (
      <DeveloperPortalRootContext.Provider value={props}>
        <ReduxProvider store={store}>
          <WithLocaleProvider {...props} />
        </ReduxProvider>
      </DeveloperPortalRootContext.Provider>
    );
  })(appRootPromise);

  const container = document.getElementById('root') as HTMLDivElement;

  // The container element will exist or we cannot render.
  // Instead of doing crazy fallbacks for when the 'root' element is missing, just assume it is there and move on.

  const root = createRoot(container);
  root.render(
    <Suspense fallback={<StartupLoadingScreen />}>
      <DeveloperPortal />
    </Suspense>
  );
}

try {
  main();
} catch (e) {
  //TODO(nibeauli): this should display some sort of error message to the user so they can report it.
  console.error('Failed to start the Developer Portal:', e);
}
